/**
 * CustomizeSheet — Q&A refinements: mobile bottom dock, desktop fixed rail
 *
 * Mobile: short bottom surface (no click-blocking overlay) so the plan stays scrollable;
 * taps on the page behind the dock still close it via `useRefinementDockClickOutside`.
 * Desktop: fixed right rail with optional header offset (HEADER_HEIGHT) so the plan
 * can be read while answering (parent adds matching padding-right on the main column).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Drawer,
  Group,
  NumberInput,
  Progress,
  Stack,
  Text,
  TextInput,
  Transition,
  UnstyledButton,
} from '@mantine/core'
import { useReducedMotion } from '@mantine/hooks'
import {
  IconArrowRight,
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react'
import { useRefinementDockClickOutside } from '../hooks/useRefinementDockClickOutside'
import { REFINEMENT_SURFACE_Z_INDEX } from '../constants/refinement-layout'
import { AnonymousRefinementBar } from './AnonymousRefinementBar'
import { RefinementRailPanel } from './RefinementRailPanel'
import type { RefinementQuestion } from '../types'

export interface AnonymousRefinementSheetOptions {
  count: number
  showBar: boolean
  atLimit: boolean
  templateSlug: string
  display: 'capped' | 'total'
  accountMode: 'anon' | 'signedIn'
  /** Called before fallback sign-up/sign-in navigation (e.g. `markSignupFromGuide`) when explicit handlers are omitted. */
  onBeforeAuthNavigation?: () => void
  onSaveProgress?: () => void
  onSignUp?: () => void
  onSignIn?: () => void
}

interface CustomizeSheetProps {
  isOpen: boolean
  onClose: () => void
  questions: Array<RefinementQuestion>
  answers: Record<string, string>
  onAnswerChange: (key: string, value: string) => void
  /** Story 2.3 — when set, show usage + gate for anonymous users */
  anonymousRefinement?: AnonymousRefinementSheetOptions
  /**
   * `drawer` — bottom sheet (default). `rail` — fixed right column on large screens; parent
   * should add matching horizontal padding so the article does not sit under the rail.
   * `embedded` — body only, for use inside `RefinePlanSheet` (single shell + tabs).
   */
  layout?: 'drawer' | 'rail' | 'embedded'
  /** When the guided flow closes with every question answered (sheet dismiss or Done). */
  onGuidedQuestionnaireCompleted?: (payload: {
    answerCount: number
    timeSpentSeconds: number
  }) => void
}

export function CustomizeSheet({
  isOpen,
  onClose,
  questions,
  answers,
  onAnswerChange,
  anonymousRefinement,
  layout = 'drawer',
  onGuidedQuestionnaireCompleted,
}: CustomizeSheetProps) {
  const navigate = useNavigate()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('left')
  const [isTransitioning, setIsTransitioning] = useState(false)
  
  // SSR-safe: use false as initial value, getInitialValueInEffect: false ensures
  // server and first client render match (both false), then real value applies after hydration
  const prefersReducedMotion = useReducedMotion(false, { getInitialValueInEffect: false })

  const transitionDuration = prefersReducedMotion ? 0 : 150

  const sheetOpenedAtRef = useRef<number | null>(null)
  useEffect(() => {
    if (isOpen) {
      sheetOpenedAtRef.current = Date.now()
    } else {
      sheetOpenedAtRef.current = null
    }
  }, [isOpen])

  const question = questions[currentIndex] as RefinementQuestion | undefined
  const currentAnswer = question ? answers[question.key] ?? '' : ''
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0
  const isLastQuestion = currentIndex >= questions.length - 1
  const isFirstQuestion = currentIndex === 0

  const handleNext = useCallback(() => {
    if (isTransitioning || isLastQuestion) return
    setSlideDirection('left')
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentIndex((prev) => prev + 1)
      setIsTransitioning(false)
    }, transitionDuration)
  }, [isTransitioning, isLastQuestion, transitionDuration])

  const handleBack = useCallback(() => {
    if (isTransitioning || isFirstQuestion) return
    setSlideDirection('right')
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentIndex((prev) => prev - 1)
      setIsTransitioning(false)
    }, transitionDuration)
  }, [isTransitioning, isFirstQuestion, transitionDuration])

  const handleDone = useCallback(() => {
    const allAnswered =
      questions.length > 0 &&
      questions.every((q) => (answers[q.key] ?? '').trim() !== '')
    if (allAnswered && onGuidedQuestionnaireCompleted) {
      const timeSpentSeconds = sheetOpenedAtRef.current
        ? Math.round((Date.now() - sheetOpenedAtRef.current) / 1000)
        : 0
      onGuidedQuestionnaireCompleted({
        answerCount: questions.length,
        timeSpentSeconds,
      })
    }
    setCurrentIndex(0)
    onClose()
  }, [answers, onClose, onGuidedQuestionnaireCompleted, questions])

  const dockShellRef = useRefinementDockClickOutside(isOpen, handleDone)

  const goSignUp = useCallback(() => {
    anonymousRefinement?.onBeforeAuthNavigation?.()
    navigate({
      to: '/sign-up',
      search: { redirect_url: `/diy/${anonymousRefinement?.templateSlug ?? ''}` },
    })
  }, [anonymousRefinement, navigate])

  const goSignIn = useCallback(() => {
    anonymousRefinement?.onBeforeAuthNavigation?.()
    navigate({
      to: '/sign-in',
      search: { redirect_url: `/diy/${anonymousRefinement?.templateSlug ?? ''}` },
    })
  }, [anonymousRefinement, navigate])

  const handleSelectOption = useCallback(
    (value: string) => {
      if (!question || isTransitioning) return
      onAnswerChange(question.key, value)

      // Auto-advance for single choice and boolean
      if (question.questionType === 'single_choice' || question.questionType === 'boolean') {
        if (isLastQuestion) {
          setTimeout(handleDone, transitionDuration)
        } else {
          setSlideDirection('left')
          setIsTransitioning(true)
          setTimeout(() => {
            setCurrentIndex((prev) => prev + 1)
            setIsTransitioning(false)
          }, transitionDuration)
        }
      }
    },
    [question, isTransitioning, onAnswerChange, isLastQuestion, transitionDuration, handleDone]
  )

  const renderQuestionInput = () => {
    if (!question) return null

    const options = question.stringOptions?.map((o) => ({
      value: o.value,
      label: o.label,
    }))

    // Single choice - tappable options with auto-advance
    if (question.questionType === 'single_choice' && options) {
      return (
        <Stack gap="xs">
          {options.map((opt) => (
            <UnstyledButton
              key={opt.value}
              onClick={() => handleSelectOption(opt.value)}
              style={{
                padding: 'var(--mantine-spacing-sm) var(--mantine-spacing-md)',
                borderRadius: 'var(--mantine-radius-md)',
                border: `2px solid ${
                  currentAnswer === opt.value
                    ? 'var(--mantine-color-primary-filled)'
                    : 'var(--mantine-color-gray-3)'
                }`,
                backgroundColor:
                  currentAnswer === opt.value
                    ? 'var(--mantine-color-primary-light)'
                    : 'transparent',
              }}
            >
              <Group justify="space-between">
                <Text size="sm" fw={500}>
                  {opt.label}
                </Text>
                {currentAnswer === opt.value && (
                  <IconCheck size={16} color="var(--mantine-color-primary-filled)" />
                )}
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
      )
    }

    // Boolean - Yes/No toggle with auto-advance
    if (question.questionType === 'boolean') {
      return (
        <Group gap="xs">
          <UnstyledButton
            onClick={() => handleSelectOption('true')}
            style={{
              flex: 1,
              padding: 'var(--mantine-spacing-sm)',
              borderRadius: 'var(--mantine-radius-md)',
              border: `2px solid ${
                currentAnswer === 'true'
                  ? 'var(--mantine-color-primary-filled)'
                  : 'var(--mantine-color-gray-3)'
              }`,
              backgroundColor:
                currentAnswer === 'true' ? 'var(--mantine-color-primary-light)' : 'transparent',
              textAlign: 'center',
            }}
          >
            <Text size="sm" fw={500}>
              Yes
            </Text>
          </UnstyledButton>
          <UnstyledButton
            onClick={() => handleSelectOption('false')}
            style={{
              flex: 1,
              padding: 'var(--mantine-spacing-sm)',
              borderRadius: 'var(--mantine-radius-md)',
              border: `2px solid ${
                currentAnswer === 'false'
                  ? 'var(--mantine-color-primary-filled)'
                  : 'var(--mantine-color-gray-3)'
              }`,
              backgroundColor:
                currentAnswer === 'false' ? 'var(--mantine-color-primary-light)' : 'transparent',
              textAlign: 'center',
            }}
          >
            <Text size="sm" fw={500}>
              No
            </Text>
          </UnstyledButton>
        </Group>
      )
    }

    // Number input
    if (question.questionType === 'number') {
      return (
        <NumberInput
          value={currentAnswer ? Number(currentAnswer) : undefined}
          onChange={(value) => {
            onAnswerChange(question.key, value === '' ? '' : String(value))
          }}
          placeholder="Enter a number"
          size="sm"
        />
      )
    }

    // Text input
    if (question.questionType === 'text') {
      return (
        <TextInput
          value={currentAnswer}
          onChange={(e) => onAnswerChange(question.key, e.currentTarget.value)}
          placeholder="Enter your answer"
          size="sm"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              isLastQuestion ? handleDone() : handleNext()
            }
          }}
        />
      )
    }

    // Multiple choice - checkboxes
    if (question.questionType === 'multiple_choice' && options) {
      const selectedValues = currentAnswer ? currentAnswer.split('|||') : []

      const handleToggle = (value: string) => {
        const isSelected = selectedValues.includes(value)
        const newValues = isSelected
          ? selectedValues.filter((v) => v !== value)
          : [...selectedValues, value]
        onAnswerChange(question.key, newValues.join('|||'))
      }

      return (
        <Stack gap="xs">
          {options.map((opt) => {
            const isSelected = selectedValues.includes(opt.value)
            return (
              <UnstyledButton
                key={opt.value}
                onClick={() => handleToggle(opt.value)}
                style={{
                  padding: 'var(--mantine-spacing-sm) var(--mantine-spacing-md)',
                  borderRadius: 'var(--mantine-radius-md)',
                  border: `2px solid ${
                    isSelected
                      ? 'var(--mantine-color-primary-filled)'
                      : 'var(--mantine-color-gray-3)'
                  }`,
                  backgroundColor: isSelected
                    ? 'var(--mantine-color-primary-light)'
                    : 'transparent',
                }}
              >
                <Group gap="xs">
                  <Checkbox checked={isSelected} readOnly size="xs" />
                  <Text size="sm" fw={500}>
                    {opt.label}
                  </Text>
                </Group>
              </UnstyledButton>
            )
          })}
        </Stack>
      )
    }

    return null
  }

  // Determine if we need a manual Next button (text, number, multiple_choice)
  const needsManualNext =
    question?.questionType === 'text' ||
    question?.questionType === 'number' ||
    question?.questionType === 'multiple_choice'

  const dockPaddingBottom = 'calc(5.5rem + env(safe-area-inset-bottom, 0px))'

  const contentBody = (
    <>
        {anonymousRefinement?.showBar && !anonymousRefinement.atLimit && (
          <Box mb="sm">
            <AnonymousRefinementBar
              count={anonymousRefinement.count}
              display={anonymousRefinement.display}
              accountMode={anonymousRefinement.accountMode}
              density="comfortable"
              onSaveProgress={anonymousRefinement.onSaveProgress}
              onSignUp={anonymousRefinement.onSignUp}
              onSignIn={anonymousRefinement.onSignIn}
            />
          </Box>
        )}
        {anonymousRefinement?.atLimit && (
          <Alert
            color="primary"
            variant="light"
            mb="md"
            title="You've built your plan! Save it before you go"
            data-testid="anonymous-refinement-limit-alert"
          >
            <Group gap="sm" grow wrap="wrap">
              <Button
                size="sm"
                onClick={() =>
                  anonymousRefinement.onSignUp ? anonymousRefinement.onSignUp() : goSignUp()
                }
              >
                Sign up
              </Button>
              <Button
                size="sm"
                variant="default"
                onClick={() =>
                  anonymousRefinement.onSignIn ? anonymousRefinement.onSignIn() : goSignIn()
                }
              >
                Sign in
              </Button>
            </Group>
          </Alert>
        )}
        {/* Progress bar (wizard position) */}
        <Group justify="space-between" mb="xs">
          <Text size="xs" c="dimmed" data-testid="customize-sheet-question-progress">
            Question {currentIndex + 1} of {questions.length}
          </Text>
          <Button variant="subtle" color="gray" size="compact-xs" onClick={handleDone}>
            Done
          </Button>
        </Group>
        <Progress value={progress} size={4} radius="xl" mb="md" />

        {/* Question content with transition */}
        <Box style={{ minHeight: 120, overflow: 'hidden' }}>
          <Transition
            mounted={!isTransitioning}
            transition={slideDirection === 'left' ? 'slide-left' : 'slide-right'}
            duration={transitionDuration}
          >
            {(styles) => (
              <Stack style={styles} gap="sm">
                {question && (
                  <>
                    <Box>
                      <Text size="sm" fw={600}>
                        {question.question}
                      </Text>
                      {question.description && (
                        <Text size="xs" c="dimmed">
                          {question.description}
                        </Text>
                      )}
                    </Box>
                    {renderQuestionInput()}
                  </>
                )}
              </Stack>
            )}
          </Transition>
        </Box>

        {/* Navigation footer */}
        <Group justify="space-between" mt="sm">
          <Button
            variant="subtle"
            color="gray"
            size="compact-sm"
            leftSection={<IconChevronLeft size={14} />}
            onClick={handleBack}
            disabled={isFirstQuestion}
          >
            Back
          </Button>
          {needsManualNext ? (
            <Button
              size="compact-sm"
              rightSection={isLastQuestion ? <IconCheck size={14} /> : <IconArrowRight size={14} />}
              onClick={isLastQuestion ? handleDone : handleNext}
            >
              {isLastQuestion ? 'Done' : 'Next'}
            </Button>
          ) : (
            <Button
              variant="subtle"
              color="gray"
              size="compact-sm"
              rightSection={<IconChevronRight size={14} />}
              onClick={isLastQuestion ? handleDone : handleNext}
            >
              {isLastQuestion ? 'Done' : 'Skip'}
            </Button>
          )}
        </Group>
    </>
  )

  if (layout === 'embedded') {
    if (!isOpen) return null
    return <Box className="customize-sheet-embedded">{contentBody}</Box>
  }

  if (layout === 'rail') {
    if (!isOpen) return null
    return (
      <RefinementRailPanel
        ariaLabel="Plan personalization"
        title="Personalize"
        onClose={handleDone}
        closeAriaLabel="Close personalization"
      >
        {contentBody}
      </RefinementRailPanel>
    )
  }

  return (
    <Drawer
      opened={isOpen}
      onClose={handleDone}
      position="bottom"
      size="auto"
      zIndex={REFINEMENT_SURFACE_Z_INDEX}
      title={null}
      withCloseButton={false}
      withOverlay={false}
      lockScroll={false}
      trapFocus={false}
      closeOnClickOutside={false}
      styles={{
        content: {
          borderTopLeftRadius: 'var(--mantine-radius-lg)',
          borderTopRightRadius: 'var(--mantine-radius-lg)',
          maxHeight: '40vh',
          boxShadow:
            'light-dark(0 -0.25rem 1.25rem rgba(0, 0, 0, 0.12), 0 -0.25rem 1.25rem rgba(0, 0, 0, 0.45))',
        },
        body: {
          padding: 0,
        },
      }}
    >
      <Box ref={dockShellRef}>
        <Group justify="center" py="xs">
          <Box
            w={40}
            h={4}
            bg="gray.4"
            style={{ borderRadius: 'var(--mantine-radius-full)' }}
          />
        </Group>

        <Box px="md" pb="md" style={{ paddingBottom: dockPaddingBottom }}>
          {contentBody}
        </Box>
      </Box>
    </Drawer>
  )
}
